// frontend/src/components/CategoryPage/CategoryPage.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import ArtworkCard from "./ArtworkCard";
import LoadingSpinner from "../common/LoadingSpinner";
import "./CategoryPage.css";

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const observerRef = useRef();
  const lastArtworkRef = useRef();

  // Load category details
  useEffect(() => {
    const loadCategory = async () => {
      try {
        const data = await api.getCategoryById(categoryId);
        setCategory(data);
      } catch (err) {
        setError("Category not found");
        console.error(err);
      }
    };
    loadCategory();
  }, [categoryId]);

  // Reset when category changes
  useEffect(() => {
    setArtworks([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
  }, [categoryId]);

  // Load artworks when page changes
  useEffect(() => {
    const loadArtworks = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getArtworks(categoryId, page);
        setArtworks((prev) => [...prev, ...data.artworks]);
        setHasMore(data.hasMore);
      } catch (err) {
        setError("Failed to load artworks");
        console.error(err);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };
    loadArtworks();
  }, [categoryId, page]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading || !hasMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((p) => p + 1);
      }
    });

    observerRef.current = observer;
    if (lastArtworkRef.current) {
      observer.observe(lastArtworkRef.current);
    }

    return () => observer.disconnect();
  }, [loading, hasMore]);

  const handleSelectArtwork = (artwork) => {
    navigate(`/artwork/${artwork._id}`, {
      state: {
        artworkList: artworks,
        categoryId,
        categoryName: category?.name,
      },
    });
  };

  if (initialLoading) {
    return <LoadingSpinner message="Loading artworks..." />;
  }

  return (

      <div className="category-main">
        <div className="category-content">
          <h1 className="category-title">{category?.name || "Loading..."}</h1>

          {error && <div className="category-error">{error}</div>}

          <div className="artworks-grid">
            {artworks.map((artwork, idx) => (
              <ArtworkCard
                key={artwork._id}
                artwork={artwork}
                onClick={() => handleSelectArtwork(artwork)}
                ref={idx === artworks.length - 1 ? lastArtworkRef : null}
              />
            ))}
          </div>

          {loading && (
            <div className="category-loading">
              <LoadingSpinner />
            </div>
          )}

          {!loading && artworks.length === 0 && (
            <div className="category-empty">
              No artworks found in this category.
            </div>
          )}
        </div>
      </div>
  );
};

export default CategoryPage;
